import { LOCALE_ID, NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { GtdNgWidgetModule } from 'gtd-ng-widget';
import { AppRoutingModule } from './app-routing.module';
import { UserResolverService } from './core/services/auth/user-resolver.service';
import { DataResolverService } from './core/services/data/data-resolver.service';
import { ConfigurationResolverService } from './core/services/configuration/configuration.resolver.service';
import { ENVIRONMENT, EnvironmentService } from './core/services/environment/environment.service';
import { AuthService } from './core/services/auth/auth.service';
import { ActionsService } from './core/services/actions/actions.service';
import { IMODALS_SERVICE, INOTIFICATIONS_SERVICE, ModalsService, TIMEZONE_CONFIG } from '@platform500services/p500-ui-kit';
import { DataService } from './core/services/data/data.service';
import { EventsService } from './core/services/events/events.service';
import { PageService } from './core/services/page/page.service';
import { RbacService } from './core/services/rbac/rbac.service';
import { ConfigurationService } from './core/services/configuration/configuration.service';
import { ApplicationService } from './core/services/applications/application.service';
import { SidebarService } from './core/services/sidebar/sidebar.service';
import { AuthGuard } from './core/guards/auth.guard';
import { GuestGuard } from './core/guards/guest.guard';
import { CanGuard } from './core/services/rbac/guards/can.guard';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { BrowserWindowRef } from './core/services/window/window.service';
import { BaseModule } from './core/layouts/base/base.module';
import { AuthLayoutModule } from './core/layouts/auth/auth-layout.module';
import { NotificationsService } from '@platform500services/p500-ui-kit';
import { ClipboardService } from './core/services/clipboard/clipboard.service';
import { UnsecuredService } from './core/services/unsecured/unsecured.service';
import { IconsService } from './core/services/icons/icons.service';
import { ErrorHandlerService } from './core/services/error-handler/error-handler.service';
import { MetricsService } from './modules/settings/crm-metrics/services/metrics.service';
import { TimeZoneConfig } from './core/time-zone/time-zone.config';
import { AnalyticsComponent } from './core/components/analytics/analytics.component';
import { SettingsService } from './core/services/settings/settings.service';
import { POPUPS_SERVICE } from './core/components/popup-dropdown-button/popups-service/popups-service.interface';
import { PopupsService } from './core/components/popup-dropdown-button/popups-service/popups.service';
import { activityInterceptor } from './core/interceptors/activity.interceptor';

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    GtdNgWidgetModule.forRoot(environment.services),
    BaseModule,
    AuthLayoutModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    AnalyticsComponent,
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    UserResolverService,
    DataResolverService,
    ConfigurationResolverService,
    AuthGuard,
    GuestGuard,
    CanGuard,
    BrowserWindowRef,
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: 'IEnvironmentService',
      useClass: EnvironmentService,
    },
    {
      provide: 'AuthServiceInterface',
      useClass: AuthService,
    },
    {
      provide: 'ActionsServiceInterface',
      useClass: ActionsService,
    },
    {
      provide: IMODALS_SERVICE,
      useClass: ModalsService,
    },
    {
      provide: 'IDataService',
      useClass: DataService,
    },
    {
      provide: 'EventsServiceInterface',
      useClass: EventsService,
    },
    {
      provide: 'ISettingsService',
      useClass: SettingsService,
    },
    {
      provide: INOTIFICATIONS_SERVICE,
      useClass: NotificationsService,
    },
    {
      provide: 'PageServiceInterface',
      useClass: PageService,
    },
    {
      provide: 'RbacServiceInterface',
      useClass: RbacService,
    },
    {
      provide: 'IConfigurationService',
      useClass: ConfigurationService,
    },
    {
      provide: 'IApplicationsService',
      useClass: ApplicationService,
    },
    {
      provide: 'ISidebarService',
      useClass: SidebarService,
    },
    {
      provide: 'ClipboardServiceInterface',
      useClass: ClipboardService,
    },
    {
      provide: 'IUnsecuredService',
      useClass: UnsecuredService,
    },
    {
      provide: 'IIconsService',
      useClass: IconsService,
    },

    {
      provide: 'IMetricsService',
      useClass: MetricsService,
    },
    {
      provide: POPUPS_SERVICE,
      useClass: PopupsService,
    },
    { provide: LOCALE_ID, useValue: 'locale' },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: TIMEZONE_CONFIG,
      useFactory: () => new TimeZoneConfig(),
    },
    provideHttpClient(withInterceptors([activityInterceptor])),
  ],
})
export class AppModule {}
